/**
 * Assessment payload types + shared vocabulary (DC-1794, demo D4).
 *
 * Mirrors `server/banking/assessment/runReadModel`. The outcome vocabulary is
 * declared once here so Results, Decision, and History cannot label the same
 * outcome three different ways — the labels are as load-bearing as the
 * numbers, because `not_assessable` reading as "fail" anywhere is the exact
 * lie the honesty rules exist to prevent.
 */

export type FindingOutcome =
  | 'satisfied'
  | 'marginal'
  | 'not_satisfied'
  | 'attestation_required'
  | 'not_assessable'
  | 'collector_error';

export interface OutcomeCounts {
  satisfied: number;
  marginal: number;
  notSatisfied: number;
  attestationRequired: number;
  notAssessable: number;
  collectorError: number;
  measured: number;
  attested: number;
  absent: number;
  total: number;
}

/** 3LOD (DC-1805): the five-value line vocabulary — first|second|third are
 * the lines; governing is the board layer; external is beyond-the-lines
 * assurance. */
export type LineOfDefense = 'first' | 'second' | 'third' | 'governing' | 'external';

export interface AttributedActor {
  actorId: string;
  displayName: string;
  roleTitle: string;
  line: LineOfDefense;
}

/**
 * 3LOD (DC-1805, design §5.1) — SERVER-computed beside `disputedCount`; the
 * client never restates the rule. `selfAttestedOnly` is true only when the
 * finding is attested, fully attributed, and every satisfying supplier is
 * first-line; unattributed evidence makes it indeterminate ("line
 * unstated"), never true and never independent.
 */
export interface FindingAccountability {
  suppliedBy: AttributedActor[];
  linesRepresented: LineOfDefense[];
  selfAttestedOnly: boolean;
  unattributed: boolean;
}

export interface PinnedEvidence {
  itemId: string;
  kind:
    | 'document'
    | 'dataset_ref'
    | 'system_attestation'
    | 'interview_attestation'
    | 'external_artefact';
  label: string;
  sourceRef: string | null;
  interview?: { speaker: string; stance: 'attests' | 'contradicts'; quote: string; confirmed: boolean };
  external?: {
    artefactKind: string;
    issuer: string;
    asOfDate: string;
    expiresAt: string;
    expired: boolean;
    reliability: 'relied_upon' | 'not_relied_upon';
    reliabilityReason: string | null;
    /** DC-1808: stated independence, welded to its note; orthogonal to
     * reliability, both visible. Older payloads read as unstated. */
    sourceIndependence?: 'independent' | 'non_independent' | 'unstated';
    independenceNote?: string | null;
  };
  system?: { systemName: string; systemClass: string; attributesAsserted: string[] };
  /** 3LOD: the attributed actor on this satellite; absent = "line unstated". */
  attributedActor?: AttributedActor;
}

export interface RunFinding {
  id: string;
  testCode: string;
  controlCode: string;
  leg: string | null;
  outcome: FindingOutcome;
  band: 'pass' | 'amber' | 'fail' | null;
  score: number | null;
  confidence: 'measured' | 'attested' | 'absent';
  collectorId: string;
  collectorVersion: string;
  /** The artefact the finding was produced from, when there is a single one. */
  sourceRef: string | null;
  detail: Record<string, unknown> | null;
  observedAt: string;
  /** What would answer this test — the catalog's own evidence description. */
  expects: string | null;
  /** Catalog names beside the identifiers (DC-1807), entitlement-joined at
   * read time like `expects`; null = the identifier stands alone. */
  testName: string | null;
  controlName: string | null;
  evidence: PinnedEvidence[];
  /** 3LOD (DC-1805): server-derived — see FindingAccountability. Optional-
   * typed (the `verifications` precedent): a payload from a pre-170 server
   * during a deploy window degrades to no overlay, never to a crash. */
  accountability?: FindingAccountability;
}

/**
 * DC-1823 (design §3.5): a concurrence or dissent recorded beside a decision.
 * Newest-last, full history visible; a superseded decision keeps its
 * positions. A flag, never a block — no state machine anywhere.
 */
export interface DecisionPosition {
  id: string;
  actor: AttributedActor;
  position: 'concur' | 'dissent';
  basis: string;
  recordedByName: string;
  createdAt: string;
}

export interface RunDecision {
  id: string;
  judgement: string;
  rationale: string;
  owner: string;
  /** DC-1823: the owner as a registry actor, when bound at INSERT. Optional-
   * typed (the `accountability` precedent): a payload from a pre-slice-2
   * server during a deploy window degrades to the free-text owner alone. */
  ownerActor?: AttributedActor | null;
  decidedByName: string;
  decidedAt: string;
  supersededBy: string | null;
  /** DC-1823: positions on this decision, newest-last. Optional-typed for
   * the same deploy-window reason — absent renders as no positions chrome. */
  positions?: DecisionPosition[];
}

/**
 * DC-1824 (design §3.6) — the accountability map.
 *
 * `external` is absent from this vocabulary on purpose: external assurance
 * attaches to evidence, not to standing accountability. The actor's OWN
 * registered line rides on `actor.line` and may differ from the line they are
 * assigned to — the map shows both rather than smoothing the difference away.
 */
export const ACCOUNTABILITY_LINES = ['first', 'second', 'third', 'governing'] as const;

export type AccountabilityLine = (typeof ACCOUNTABILITY_LINES)[number];

export interface AccountabilityAssignment {
  id: string;
  line: AccountabilityLine;
  actor: AttributedActor;
  assignedByName: string;
  createdAt: string;
}

export interface AccountabilityLineEntry {
  line: AccountabilityLine;
  /** Empty means NOBODY is assigned — rendered as a stated absence, which is
   * the whole point of the panel. Never rendered as blank. */
  assignments: AccountabilityAssignment[];
}

export interface AccountabilityMap {
  lines: AccountabilityLineEntry[];
}

/** What each line is accountable FOR — one sentence, so the panel reads to a
 * examiner without the 3LOD vocabulary having to be known already. */
export const ACCOUNTABILITY_LINE_MEANING: Record<AccountabilityLine, string> = {
  first: 'Owns the use case and its risk day to day.',
  second: 'Independent challenge and oversight of the first line.',
  third: 'Independent assurance over the whole arrangement.',
  governing: 'Board or senior-management accountability for the outcome.',
};

export interface RunHeader {
  id: string;
  status: 'queued' | 'running' | 'complete' | 'failed';
  catalogVersionId: string;
  catalogVersionLabel: string | null;
  catalogContentDigest: string;
  engineVersion: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  createdByName: string;
  stale: boolean;
}

/** The ARC risk band. `statusBand` is worse_of(riskBand, critical gate). */
export type RiskBand = 'critical' | 'elevated' | 'monitor' | 'effective';

/**
 * The persisted ARC roll-up (DC-1746) — SERVER-computed, always.
 *
 * It arrives INSIDE `RunResults`, beside the counts, and there is no route
 * that serves it alone: §2.2 makes coverage a condition of the headline
 * rather than a presentation note, and a separate endpoint would let a
 * consumer obtain the number without it.
 *
 * The client never computes, re-bands, re-rounds or re-derives any of this.
 * A client-side roll-up is a defect even when the arithmetic is right.
 */
export interface RunRollup {
  status: 'computed' | 'refused' | 'not_attempted';
  useCaseRisk: number | null;
  /** The top of the scale. The figure is never shown without it. */
  riskScaleMax: number | null;
  riskBand: RiskBand | null;
  statusBand: RiskBand | null;
  criticalGateFired: boolean | null;
  materialityTier: string | null;
  tierMultiplier: number | null;
  systemicUpliftApplied: number | null;
  controlScoreCeiling: number | null;
  assessedControlCount: number | null;
  unassessedControlCount: number | null;
  /** Zero means the four named hard gates cannot fire. Stated, never hidden. */
  hardGateBindingCount: number | null;
  refusalReason: string | null;
  refusalDetail: string | null;
  methodVersion: string;
  computedAt: string;
}

export interface RunResults {
  run: RunHeader;
  counts: OutcomeCounts;
  /** Null when nothing has been computed for this run at all. */
  rollup: RunRollup | null;
  /** DC-1806: findings a confirmed attestation contradicts — server-computed,
   * the client never restates the counting rule. */
  disputedCount: number;
  /** DC-1805: findings whose only satisfying attestations are first-line
   * actors — a count, never a percentage; server-computed. */
  selfAttestedOnlyCount: number;
  findings: RunFinding[];
  decisions: RunDecision[];
}

export interface RunHistoryEntry extends RunHeader {
  counts: OutcomeCounts;
  /** DC-1806: disputes existed on this run — carried into History always. */
  disputedCount: number;
  decision: { judgement: string; owner: string; decidedAt: string } | null;
}

export interface RunHistory {
  currentCatalog: { versionId: string; versionLabel: string; contentDigest: string } | null;
  runs: RunHistoryEntry[];
}

/**
 * One label per outcome, and a `tone` that drives colour. `marginal` has its
 * OWN tone: rendering amber as pass or fail is a fixture failure, not a
 * styling preference (FR-4.2 AC fail).
 */
export const OUTCOME_VOCABULARY: Record<
  FindingOutcome,
  { label: string; tone: 'good' | 'soft' | 'hard' | 'quiet'; meaning: string }
> = {
  satisfied: {
    label: 'Satisfied',
    tone: 'good',
    meaning: 'The evidence answers this test at or above the bar ARC sets.',
  },
  marginal: {
    label: 'Marginal',
    tone: 'soft',
    meaning: 'Inside the tolerance band ARC defines — neither a pass nor a failure.',
  },
  not_satisfied: {
    label: 'Not satisfied',
    tone: 'hard',
    meaning: 'The evidence answers this test below the bar, or contradicts it.',
  },
  attestation_required: {
    label: 'Attestation required',
    tone: 'quiet',
    meaning:
      'No machine evaluation exists for this test, so a person must answer it. A human answer is a complete result, not a gap.',
  },
  not_assessable: {
    label: 'Not assessable',
    tone: 'quiet',
    meaning:
      'Nothing on the record could answer this test. It is not a failed control — it is an unanswered one.',
  },
  collector_error: {
    label: 'Collector error',
    tone: 'hard',
    meaning: 'The collector for this test failed. The run stands; this one finding does not.',
  },
};

export const CONFIDENCE_LABEL: Record<'measured' | 'attested' | 'absent', string> = {
  measured: 'measured by Compass',
  attested: 'attested by a person',
  absent: 'no evidence',
};

/** One label per line — Results, Decision, and Evidence share it. */
export const LINE_LABEL: Record<LineOfDefense, string> = {
  first: 'first line',
  second: 'second line',
  third: 'third line',
  governing: 'governing layer',
  external: 'external assurance',
};

/** Short, stable reasons for evidence that was present but did not count. */
export const DISREGARD_LABEL: Record<string, string> = {
  unconfirmed_candidate: 'unconfirmed candidate — not evidence until a human confirms it',
  expired: 'past its 12-month horizon',
  not_relied_upon: 'marked not relied upon by the reviewer',
  awaiting_data_collector: 'awaiting the data collector',
  entry_not_bandable: 'the catalog no longer bands this entry',
};
